import { FC, useEffect, useMemo, useState } from "react";
import BillingFormData from "./types/billing-form.data";
import billingDataRouteRequestsFactory from "./factory/billing-data-route-requests.factory";
import billingMapRoutesFactory from "./factory/billing-map-routes.factory";
import billingDataFactory from "./factory/billing-data.factory";
import BillingDetailsComponent from "./billing-details/billing-details.component";
import ContractDetailsComponent from "./contract-details/contract-details.component";
import MapRoute from "../../../../common/components/map/types/map-route";
import MapMarker from "../../../../common/components/map/types/map-marker";
import Row from "../../../../common/components/grid/row";
import Column from "../../../../common/components/grid/column";
import MapComponent from "../../../../common/components/map/map.component";
import CardComponent from "../../../../common/components/card/card.component";
import SearchRoadRoutingResponse from "../../../../common/utils/search-road-route/search-road-routing.response";
import billingsTranslationsHelper from "../../../../languages/billings-translations.helper";
import { useAppContext } from "../../../../context/app.context";
import { useParams } from "react-router-dom";
import appTranslationsHelper from "../../../../languages/app-translations.helper";
import useDocumentTitle from "../../../../common/hooks/use-document-title";
import billingBreadcrumbsHelper from "../../common/breadcrumbs/billings-breadcrumbs.helper";
import HeadingComponent from "../../../../common/components/heading/heading.component";
import BillingTaxiDriverDetailsRouteParams from "../../common/routes/types/billing-taxi-driver-details-route-params";
import RelatedBillingsCargoTaxiComponent from "../common/related-billings/cargo-taxi/related-billings-cargo-taxi.component";
import RelatedBillingsTaxiTaxiComponent from "../common/related-billings/taxi-taxi/related-billings-taxi-taxi.component";
import BillingsRouteDetailsOverviewComponent from "../../common/route-details/billings-route-details-overview.component";
import MessengerComponent from "../../../../common/components/messenger/messenger.component";
import billingsTaxiDriverHelper from "./billings-taxi-driver.helper";
import billingsMapMarkersFactory from "../../common/map-markers/billings-map-markers.factory";
import BillingsToolsComponent from "../../common/tools/billings-tools.component";
import OrderDetailsHistoryComponent from "../../../order/details/history/order-details-history.component";
import TabsComponent from "../../../../common/components/tabs/tabs.component";
import TabsData from "../../../../common/components/tabs/common/types/tabs-data";
import ButtonComponent from "../../../../common/components/button/button.component";
import ContentWrapper from "../../../../common/components/content-wrapper/content-wrapper.component";
import BillingRouteDetailsComponent from "./billing-route-details/billing-route-details.component";
import dateService from "../../../../common/utils/date/date.service";
import mapMarkerIconFactory from "../../../../common/components/map/marker/map-marker-icon.factory";
import billingsMapRouteFactory from "./factory/billings-map-route.factory";
import BillingModel from "../../types/billing-model";
import useGpsPlanEntryRecords from "../../../../common/services/gps/plan-entry-records/use-gps-plan-entry-records";
import useAbort from "../../../../common/hooks/use-abort";
import GpsPlanEntryRecordsParams from "../../../../common/services/gps/plan-entry-records/gps-plan-entry-records-params";
import { GpsPlanEntryRecordsItem } from "../../../../common/services/gps/plan-entry-records/gps-plan-entry-records";
import searchRoadRoutingService from "../../../../common/utils/search-road-route/search-road-routing.service";
import useBillingsTaxiDriverDetails from "../../../../common/services/billings/taxi-driver/details/use-billings-taxi-driver-details";
import BillingsTaxiDriverDetailsParams from "../../../../common/services/billings/taxi-driver/details/billings-taxi-driver-details-params";

type BillingComponentProps = {
  asCardComponent?: boolean;
};

const BillingsTaxiDriverViewComponent: FC<BillingComponentProps> = (props) => {
  const { billingUuid } = useParams<BillingTaxiDriverDetailsRouteParams>();

  const { setBreadcrumbs, selectedAppLanguage } = useAppContext();

  const [billingData, setBillingData] = useState<BillingFormData>();
  const [plannedMapRoute, setPlannedMapRoute] = useState<MapRoute | null>(null);
  const [completedMapRoute, setCompletedMapRoute] = useState<MapRoute | null>(
    null,
  );

  const [shouldShowCompletedMapRoutes, setShouldShowCompletedMapRoutes] =
    useState(false);

  const [hoveredMarker, setHoveredMarker] = useState<MapMarker | null>(null);

  const [completedRouteData, setCompletedRouteData] = useState<
    GpsPlanEntryRecordsItem[]
  >([]);

  const [routeId, setRouteId] = useState("");
  const [shouldRetrieveRouteData, setShouldRetrieveRouteData] = useState(false);
  const [planEntryUuid, setPlanEntryUuid] = useState("");
  const [shouldShowOrderHistory, setShouldShowOrderHistory] = useState(false);

  const gpsPlanEntryRecords = useGpsPlanEntryRecords();
  const gpsPlanEntryRecordsAbort = useAbort();

  const billingsTaxiDriverDetails = useBillingsTaxiDriverDetails();
  const billingsTaxiDriverDetailsAbort = useAbort();

  const documentTitle = appTranslationsHelper
    .getDocumentTitleTranslations()
    .billingsTaxiWithDriverView.replace("#{routeId}", routeId);

  useDocumentTitle(documentTitle);

  useEffect(() => {
    const breadcrumbs =
      billingBreadcrumbsHelper.getTaxiWithDriverViewBreadcrumbs({
        billingUuid: billingUuid!,
        routeId,
      });

    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage, routeId]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    if (!billingUuid) return;

    const params: BillingsTaxiDriverDetailsParams = {
      billingUuid: billingUuid,
    };

    billingsTaxiDriverDetails.load(
      params,
      billingsTaxiDriverDetailsAbort.signal,
    );
  };

  useEffect(() => {
    if (!planEntryUuid) {
      return;
    }

    const params: GpsPlanEntryRecordsParams = {
      planEntryUuid: planEntryUuid,
    };

    gpsPlanEntryRecords.load(params, gpsPlanEntryRecordsAbort.signal);

    return gpsPlanEntryRecordsAbort.revoke;
  }, [planEntryUuid]);

  const onCargoOrderSelectButtonClick = (cargoOrderUuid: string) => {
    setPlanEntryUuid(cargoOrderUuid);
  };

  useEffect(() => {
    if (!billingData || !shouldRetrieveRouteData) {
      return;
    }

    const routeRequests =
      billingDataRouteRequestsFactory.createBillingDataRouteRequests(
        billingData.billingNodes,
      );

    const fetchPromises: Promise<SearchRoadRoutingResponse>[] = [];

    routeRequests.forEach((routeRequest) => {
      const fetch = searchRoadRoutingService.search(routeRequest);

      fetchPromises.push(fetch);
    });

    Promise.all(fetchPromises).then((responses) => {
      const mapRouteWaypointGroups: MapRoute["waypoints"][] = [];
      const lastResponseIndex = responses.length - 1;
      let lastResponseDuration = 0;

      responses.forEach((response, index) => {
        const mapRoute = response.routes[0]
          ? billingMapRoutesFactory.createMapRoute(
              response.routes[0].geometry.coordinates,
            )
          : null;

        if (mapRoute?.waypoints) {
          mapRouteWaypointGroups.push(mapRoute.waypoints);
        }

        if (index == lastResponseIndex) {
          lastResponseDuration = response.routes[0].duration;
        }
      });

      const newMapRouteWaypoints: MapRoute["waypoints"] = [];

      mapRouteWaypointGroups.forEach((waypoint) => {
        newMapRouteWaypoints.push(...waypoint);
      });

      const newMapRoute: MapRoute = {
        waypoints: newMapRouteWaypoints,
        options: { color: "red" },
      };

      if (
        billingData.contractDetails &&
        billingData.contractDetails.model !== BillingModel.AB
      ) {
        const lastBillingNodeIndex = billingData.billingNodes.length - 1;
        const secondToLastBillingNodePlannedDate =
          billingData.billingNodes[lastBillingNodeIndex - 1].plannedDate;

        if (secondToLastBillingNodePlannedDate) {
          const lastBillingNodePlannedDate = new Date(
            new Date(secondToLastBillingNodePlannedDate).getTime() +
              1000 * lastResponseDuration,
          );

          billingData.billingNodes[lastBillingNodeIndex].plannedDate =
            lastBillingNodePlannedDate;
          billingData.billingNodes[lastBillingNodeIndex].checkoutDate =
            lastBillingNodePlannedDate;

          setBillingData(billingData);
        }
      }

      setPlannedMapRoute(newMapRoute);
      setShouldRetrieveRouteData(false);
    });
  }, [shouldRetrieveRouteData]);

  useEffect(() => {
    if (!billingsTaxiDriverDetails.data) return;

    const billingData = billingDataFactory.createBillingData(
      billingsTaxiDriverDetails.data,
    );

    setBillingData(billingData);
    setPlanEntryUuid(billingData.planEntryId);
    setShouldRetrieveRouteData(true);
    setRouteId(String(billingData.internalOrderId));
  }, [billingsTaxiDriverDetails.data]);

  useEffect(() => {
    if (!gpsPlanEntryRecords.data) return;
    setCompletedRouteData(gpsPlanEntryRecords.data.data);
    const gpsData: MapRoute = billingsMapRouteFactory.createCompletedMapRoute(
      gpsPlanEntryRecords.data.data,
    );

    setCompletedMapRoute(gpsData);
    setShouldShowCompletedMapRoutes(true);
  }, [gpsPlanEntryRecords.data]);

  useEffect(() => {
    if (completedRouteData.length > 0 && !gpsPlanEntryRecords.isLoading) {
      const gpsData: MapRoute =
        billingsMapRouteFactory.createCompletedMapRoute(completedRouteData);
      setCompletedMapRoute(gpsData);
    }
  }, [completedRouteData, gpsPlanEntryRecords.isLoading]);

  const mapMarkers: MapMarker[] = useMemo(() => {
    const mapMarkers = billingsMapMarkersFactory
      .createMapMarkers(billingData?.billingNodes ?? [])
      .map((marker) => ({ ...marker, isRouteMarker: true }));

    return mapMarkers;
  }, [billingData?.billingNodes]);

  const mapRoutes: MapRoute[] = useMemo(() => {
    const finalMapRoutes: MapRoute[] = [];
    if (plannedMapRoute) {
      finalMapRoutes.push(plannedMapRoute);
    }
    if (completedMapRoute) {
      finalMapRoutes.push(completedMapRoute);
    }
    return finalMapRoutes;
  }, [plannedMapRoute, completedMapRoute]);

  const onMarkerHover = (marker: MapMarker | null) => {
    setHoveredMarker(marker);
  };

  const generateTimeBasedMarkers = (
    completedRoute: GpsPlanEntryRecordsItem[],
    existingRouteMarkers: MapMarker[],
  ): MapMarker[] => {
    if (!completedRoute || completedRoute.length === 0) {
      return [];
    }

    const markers: MapMarker[] = [];
    const parseTime = (timestamp: Date | string): number =>
      new Date(timestamp).getTime();

    let startTime = parseTime(completedRoute[0].recordTime);
    let nextMarkerTime = startTime;

    let index = 0;

    completedRoute.forEach((currPoint) => {
      let currTime = parseTime(currPoint.recordTime);

      if (currTime >= nextMarkerTime) {
        nextMarkerTime = currTime + 60000;
        const date = new Date(currPoint.recordTime);

        if (dateService.checkIsValidDate(date)) {
          const formattedTime = dateService.formatTime(date);

          const newMarker = {
            coordinate: {
              latitude: currPoint.latitude,
              longitude: currPoint.longitude,
            },
            icon: mapMarkerIconFactory.createGpsIcon(),
            title: formattedTime,
            isGpsRouteMarker: true,
            onMouseOver: () =>
              onMarkerHover({
                coordinate: {
                  latitude: currPoint.latitude,
                  longitude: currPoint.longitude,
                },
                icon: mapMarkerIconFactory.createGpsIcon(),
                title: formattedTime,
              }),
            onMouseOut: () => onMarkerHover(null),
            index: index,
          };

          index++;

          const isDuplicate = existingRouteMarkers.some(
            (routeMarker) =>
              routeMarker.coordinate.latitude ===
                newMarker.coordinate.latitude &&
              routeMarker.coordinate.longitude ===
                newMarker.coordinate.longitude &&
              routeMarker.isGpsRouteMarker,
          );

          if (!isDuplicate) {
            markers.push(newMarker);
          }
        }
      }
    });

    return markers;
  };

  const memoizedMarkers = useMemo(() => {
    if (!completedMapRoute) {
      return (
        <MapComponent
          key={
            hoveredMarker?.coordinate.latitude +
            "-" +
            hoveredMarker?.coordinate.longitude
          }
          markers={mapMarkers}
          autoFit
          routes={mapRoutes}
        />
      );
    }

    const gpsMarkers: MapMarker[] = generateTimeBasedMarkers(
      completedRouteData,
      mapMarkers,
    );

    return (
      <MapComponent
        key={
          hoveredMarker?.coordinate.latitude +
          "-" +
          hoveredMarker?.coordinate.longitude
        }
        markers={mapMarkers}
        gpsMarkers={gpsMarkers}
        autoFit
        routes={mapRoutes}
        shouldShowCompletedMapRoutes={shouldShowCompletedMapRoutes}
      />
    );
  }, [shouldShowCompletedMapRoutes, completedRouteData, mapMarkers, mapRoutes]);

  const translations =
    billingsTranslationsHelper.getTaxiDriverViewBillingsTranslations();

  const messengerChannelsAvailability =
    billingsTaxiDriverHelper.getMessengerChannelAvailability();

  const tabsData: TabsData = useMemo(() => {
    if (!billingData) {
      return [];
    }

    return billingDataFactory.createTabsData(
      billingData.billingContributions,
      billingsTaxiDriverDetails.isLoading,
      billingData,
    );
  }, [billingData]);

  const onOrderHistoryButtonClick = () => {
    if (shouldShowOrderHistory) {
      setShouldShowOrderHistory(false);
      return;
    }

    if (!billingData?.solvedOrderUuids) {
      return;
    }

    if (billingData?.solvedOrderUuids?.length > 0) {
      const firstOrderUuid = billingData.solvedOrderUuids[0];

      setPlanEntryUuid(firstOrderUuid);
    }

    setShouldShowOrderHistory(true);
  };

  const billingsTaxiDriverToolsProps = {
    shouldShowOrderHistory,
    onOrderHistoryButtonClick,
  };

  return (
    <>
      <div className="billings">
        <HeadingComponent
          title={translations.header.headingText.replace("#{routeId}", routeId)}
        />
        <Row>
          <Column lg={8}>
            <Row>
              <Column withPaddings>
                <div className="billings_map_wrapper">
                  {memoizedMarkers}
                  <BillingsToolsComponent {...billingsTaxiDriverToolsProps} />
                </div>
              </Column>
              <Column withPaddings>
                <ContractDetailsComponent
                  isLoading={billingsTaxiDriverDetails.isLoading}
                  contractDetails={billingData?.contractDetails ?? null}
                />
              </Column>
              <Column withPaddings>
                <Row>
                  <Column lg={12}>
                    <BillingsRouteDetailsOverviewComponent
                      billingNodes={billingData?.billingNodes}
                    />
                  </Column>
                </Row>
              </Column>
              <Column withPaddings>
                <BillingRouteDetailsComponent billingData={billingData} />
              </Column>
              {!!billingData && (
                <Column withPaddings>
                  <TabsComponent data={tabsData} />
                </Column>
              )}
              <Column withPaddings>
                <BillingDetailsComponent billingData={billingData} />
              </Column>
              <Column withPaddings>
                <RelatedBillingsCargoTaxiComponent
                  planEntryId={planEntryUuid}
                />
              </Column>
              <Column withPaddings>
                <RelatedBillingsTaxiTaxiComponent planEntryId={planEntryUuid} />
              </Column>
            </Row>
          </Column>
          <Column lg={4} withPaddings>
            <CardComponent
              classNames={{
                root: "billings_history_messenger_container",
                content: "h-100",
              }}
            >
              {shouldShowOrderHistory ? (
                <ContentWrapper asCardComponent={props.asCardComponent}>
                  <div className="d-flex mb-2">
                    {billingData?.solvedOrderUuids.map(
                      (solvedOrderUuid, index) => {
                        const solvedOrderHumanId =
                          billingData.solvedOrderHumanIds[index];
                        const isSelected = planEntryUuid === solvedOrderUuid;

                        return (
                          <ButtonComponent
                            key={solvedOrderUuid}
                            onClick={() =>
                              onCargoOrderSelectButtonClick(solvedOrderUuid)
                            }
                            type={isSelected ? "success" : undefined}
                          >
                            {solvedOrderHumanId}
                          </ButtonComponent>
                        );
                      },
                    )}
                  </div>
                  <div className="billings_history">
                    <OrderDetailsHistoryComponent
                      key={planEntryUuid}
                      orderUuid={planEntryUuid}
                      refetchFlag={false}
                    />
                  </div>
                </ContentWrapper>
              ) : (
                !!planEntryUuid.length && (
                  <MessengerComponent
                    channelsAvailability={messengerChannelsAvailability}
                    planEntryUuid={planEntryUuid}
                  />
                )
              )}
            </CardComponent>
          </Column>
        </Row>
      </div>
    </>
  );
};

export default BillingsTaxiDriverViewComponent;
